import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import Icon from "components/ShellV2/Icon";

const TINT = {
  teal: "rec-tint-teal",
  amber: "rec-tint-amber",
  green: "rec-tint-green",
  info: "rec-tint-info",
};

function KpiText({ children, className, fit, baseFontSize, minFontSize }) {
  const valueRef = useRef(null);
  const [fontSize, setFontSize] = useState(baseFontSize);
  const fontSizeRef = useRef(fontSize);
  fontSizeRef.current = fontSize;

  useLayoutEffect(() => {
    if (!fit || !valueRef.current) return undefined;

    const fitText = () => {
      const element = valueRef.current;
      if (!element) return;

      const availableWidth = element.clientWidth;
      const currentFontSize = fontSizeRef.current;
      if (currentFontSize !== baseFontSize) element.style.fontSize = `${baseFontSize}px`;
      const contentWidth = element.scrollWidth;
      if (currentFontSize !== baseFontSize) element.style.fontSize = `${currentFontSize}px`;
      const nextFontSize =
        contentWidth > availableWidth && availableWidth > 0
          ? Math.max(
              minFontSize,
              Math.min(baseFontSize, (baseFontSize * availableWidth) / contentWidth)
            )
          : baseFontSize;

      if (nextFontSize !== currentFontSize) setFontSize(nextFontSize);
    };

    fitText();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(fitText);
    observer.observe(valueRef.current);
    return () => observer.disconnect();
  }, [fit, baseFontSize, minFontSize, children]);

  return (
    <div
      ref={valueRef}
      className={className}
      style={{
        fontSize,
        whiteSpace: fit ? "nowrap" : undefined,
        overflow: fit ? "visible" : undefined,
        textOverflow: fit ? "clip" : undefined,
      }}
    >
      {children}
    </div>
  );
}

KpiText.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  fit: PropTypes.bool,
  baseFontSize: PropTypes.number.isRequired,
  minFontSize: PropTypes.number.isRequired,
};

KpiText.defaultProps = {
  fit: false,
};

/* Compact KPI strip used at the top of list pages (port of the design
   prototype's KpiStrip). items: [{icon, value, unit, label, tint}] */
export default function KpiStrip({ items, cols, className }) {
  return (
    <div
      className={`rec-kpi-strip grid gap-rec${className ? ` ${className}` : ""}`}
      style={{ "--kpi-cols": cols || items.length }}
    >
      {items.map((it) => (
        <div key={it.label} className="rec-card rec-card-pad flex items-center gap-[14px]">
          <div className={`rec-kpi-ic ${TINT[it.tint] || TINT.teal}`}>
            <Icon name={it.icon} fill />
          </div>
          <div className="min-w-0">
            <KpiText className="rec-kpi-value" fit={it.fitValue} baseFontSize={23} minFontSize={12}>
              {it.value}
              {it.unit && <small>{it.unit}</small>}
              {it.sub && <small className="opacity-70 font-medium">{it.sub}</small>}
            </KpiText>
            <KpiText
              className="rec-kpi-label whitespace-nowrap overflow-hidden text-ellipsis"
              fit={it.fitLabel}
              baseFontSize={12.5}
              minFontSize={6}
            >
              {it.label}
            </KpiText>
          </div>
        </div>
      ))}
    </div>
  );
}

KpiStrip.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      unit: PropTypes.node,
      sub: PropTypes.node,
      fitValue: PropTypes.bool,
      fitLabel: PropTypes.bool,
      label: PropTypes.node.isRequired,
      tint: PropTypes.oneOf(["teal", "amber", "green", "info"]),
    })
  ).isRequired,
  cols: PropTypes.number,
  className: PropTypes.string,
};

KpiStrip.defaultProps = {
  cols: undefined,
  className: "",
};
